const G = "#b8960c";

const EXPERIENCE = [
  {
    role: "System Administrator",
    company: "Grover Group GmbH · Berlin",
    period: "Oct 2023 – Sep 2024",
    badge: "SYS ADMIN",
    points: [
      "Administered Jamf Pro & Intune for full Apple/Windows device lifecycle for 200+ users.",
      "Managed identity across Okta, Azure AD, Google Workspace & Microsoft 365.",
      "Developed 15+ automation scripts — reduced manual workload by 40%.",
      "Configured network infrastructure (DHCP, VPN, VLAN, Firewalls) with 99.5% uptime.",
    ],
    tags: ["Jamf Pro", "Okta", "Azure AD", "Intune"],
  },
  {
    role: "IT Technical Support",
    company: "Gorillas Technologies GmbH · Berlin",
    period: "Mar 2021 – Sep 2023",
    badge: "IT SUPPORT",
    points: [
      "Provided support for 150+ IT assets with 95% ticket resolution rate.",
      "Supported employee on/offboarding across hardware, accounts and access.",
      "Built and maintained 500+ internal knowledge base articles.",
    ],
    tags: ["Jira", "Zendesk", "macOS", "Windows"],
  },
  {
    role: "Customer Support & Operations",
    company: "Coffee Circle / Gorillas · Berlin",
    period: "Mar 2021 – Present",
    badge: "CUSTOMER OPS",
    points: [
      "Owned support queue for 200+ monthly cases with 90% customer satisfaction.",
      "Managed 1,000+ monthly interactions with 90% first-contact resolution.",
      "Maintained asset and inventory tracking with 98% accuracy.",
    ],
    tags: ["Dixa", "CRM", "Zendesk"],
  },
  {
    role: "Software Engineer",
    company: "BSc Software Engineering · Amsterdam Tech",
    period: "Mar 2023 – Nov 2026",
    badge: "SOFTWARE ENG",
    points: [
      "Developing software solutions using C/C++, Ruby, JavaScript, TypeScript and Node.js.",
      "Building automation pipelines integrating multiple SaaS APIs.",
      "Continuously expanding engineering skills across frontend and backend development.",
    ],
    tags: ["C/C++", "Ruby", "JavaScript", "TypeScript", "Node.js", "APIs"],
  },
];

const CERTS = ["Google IT Support", "System Administration", "Network & Security", "Meta API", "SQL", "Excel", "Python", "Tableau"];

function GoldTag({ children }) {
  return <span className="inline-block px-2 py-0.5 text-[11px] border rounded-sm mr-1 mt-1 font-medium" style={{ borderColor: G + "44", color: G, background: G + "11" }}>{children}</span>;
}

function DarkTag({ children }) {
  return <span className="inline-block px-2 py-0.5 text-[11px] border border-white/10 text-gray-400 bg-white/5 rounded-sm mr-1 mt-1 hover:text-yellow-500 transition-all cursor-default">{children}</span>;
}

function ExpCard({ e }) {
  return (
    <div className="relative pl-5 pb-8 border-l border-white/10 hover:border-yellow-600/50 transition-all">
      <div className="absolute -left-[5px] top-4 w-2.5 h-2.5 rounded-full" style={{ background: G }} />
      <span className="inline-block px-2 py-0.5 text-[9px] uppercase tracking-widest border rounded-sm mb-2 font-semibold" style={{ borderColor: G + "44", color: G, background: G + "11" }}>{e.badge}</span>
      <h3 style={{ fontFamily: 'Playfair Display, serif' }} className="text-white font-bold text-base mb-0.5">{e.role}</h3>
      <div className="text-xs font-medium mb-0.5" style={{ color: G }}>{e.company}</div>
      <div className="text-gray-600 text-[10px] tracking-wider mb-2 uppercase">{e.period}</div>
      <ul className="mb-2 space-y-1">
        {e.points.map((p, i) => (
          <li key={i} className="text-gray-500 text-[11px] leading-relaxed pl-3 relative">
            <span className="absolute left-0 font-bold" style={{ color: G }}>—</span>{p}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap">{e.tags.map(t => <DarkTag key={t}>{t}</DarkTag>)}</div>
    </div>
  );
}

function SectionHeader({ label, title }) {
  return (
    <div className="mb-4">
      <p className="text-xs tracking-[0.25em] uppercase mb-1 font-semibold" style={{ color: G }}>{label}</p>
      <h2 style={{ fontFamily: 'Playfair Display, serif' }} className="text-3xl sm:text-4xl font-black text-white">{title}</h2>
      <div className="w-10 h-0.5 mt-2" style={{ background: G }} />
    </div>
  );
}

export default function Experience({ refs }) {
  return (
    <section id="Experience" ref={el => (refs.current["Experience"] = el)} className="py-8">
      <SectionHeader label="Career" title="Work Experience" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
        <div>{EXPERIENCE.slice(0, 2).map(e => <ExpCard key={e.role} e={e} />)}</div>
        <div>{EXPERIENCE.slice(2).map(e => <ExpCard key={e.role} e={e} />)}</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        <div className="border border-white/8 rounded-sm p-4 sm:p-5" style={{ background: "#1a1a1a" }}>
          <h3 style={{ fontFamily: 'Playfair Display, serif' }} className="text-white font-bold text-base mb-4">Education</h3>
          <div className="mb-4 pb-4 border-b border-white/8">
            <div style={{ fontFamily: 'Playfair Display, serif' }} className="text-white font-bold text-sm">BSc Software Engineering</div>
            <div className="text-xs font-medium my-0.5" style={{ color: G }}>Amsterdam Tech, Amsterdam</div>
            <div className="text-xs text-gray-600 uppercase tracking-wide">Mar 2023 – Nov 2026</div>
          </div>
          <div>
            <div style={{ fontFamily: 'Playfair Display, serif' }} className="text-white font-bold text-sm">Higher Diploma</div>
            <div className="text-xs font-medium my-0.5" style={{ color: G }}>Royal Comprehensive College, Edo State</div>
            <div className="text-xs text-gray-600 uppercase tracking-wide">2000 – 2006</div>
          </div>
        </div>
        <div className="border border-white/8 rounded-sm p-4 sm:p-5" style={{ background: "#1a1a1a" }}>
          <h3 style={{ fontFamily: 'Playfair Display, serif' }} className="text-white font-bold text-base mb-3">Certifications</h3>
          <div className="mb-4 flex flex-wrap">{CERTS.map(c => <GoldTag key={c}>{c}</GoldTag>)}</div>
          <h3 style={{ fontFamily: 'Playfair Display, serif' }} className="text-white font-bold text-base mb-2">Languages</h3>
          <div><GoldTag>English C2</GoldTag><GoldTag>Italian B2</GoldTag><GoldTag>German B1</GoldTag></div>
        </div>
      </div>
    </section>
  );
}
